import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@app/core/services/translate.service';

@Component({
    selector: 'tt-label-translation-dialog',
    templateUrl: './label-translation-dialog.component.html',
    styleUrls: ['./label-translation-dialog.component.css'],
})
export class LabelTranslationDialogComponent {
    public activeTab: string = 'input_label';

    public labelEdits: any[] = [];
    private persistedLabelEdits: string = '';
    private hasChangedLabel = false;

    public sublabelEdits: any[] = [];
    private persistedSublabelEdits: string = '';
    private hasChangedSublabel = false;

    public helptextEdits: any[] = [];
    private persistedHelptextEdits: string = '';
    private hasChangedHelptext = false;

    public inputLabel: string;
    public inputSublabel: string;
    public inputHelptext: string;

    public sublabelTabHasBeenSelected = false;
    public helptextTabHasBeenSelected = false;

    public onTabChanged(event: string) {
        if (event === 'input_sublabel') {
            this.sublabelTabHasBeenSelected = true;
        } else if (event === 'input_helptext') {
            this.helptextTabHasBeenSelected = true;
        }
    }

    private translations: { [key: string]: string } = {
        you_have_unsaved_changes: '',
        label_changes_are_not_saved: '',
        sublabel_changes_are_not_saved: '',
        helptext_changes_are_not_saved: '',
        unsaved_changes_will_be_lost: '',
        are_you_sure_you_want_to_continue: '',
    };

    constructor(
        private translate: TranslateService,
        public dialog: MatDialogRef<LabelTranslationDialogComponent>,
        private datatask: DataTaskService,
        @Inject(MAT_DIALOG_DATA) public data: LabelTranslationData,
        private modal: ModalService,
        private toast: ToastService,
    ) {
        this.translateWords();

        this.inputLabel = data.label;
        this.inputSublabel = data.sublabel || this.inputLabel + '_sublabel';
        this.inputHelptext = data.helptext || this.inputLabel + '_helptext';

        this.dialog.beforeClosed().subscribe({
            next: () => {
                this.dialog.close({ labelChanged: this.hasChangedLabel, sublabelChanged: this.hasChangedSublabel, helptextChanged: this.hasChangedHelptext });
            },
        });

        this.loadLabelEdits();
        this.loadSublabelEdits();
        this.loadHelptextEdits();
    }

    public async close() {
        if (!this.dialog.disableClose) {
            let shouldClose = true;
            let confirmMessage = '';

            if (!this.isLabelChangesSaved()) {
                confirmMessage += this.translations['label_changes_are_not_saved'] + '\n';
            }

            if (!this.isSublabelChangesSaved()) {
                confirmMessage += this.translations['sublabel_changes_are_not_saved'] + '\n';
            }

            if (!this.isHelpttextChangesSaved()) {
                confirmMessage += this.translations['helptext_changes_are_not_saved'] + '\n';
            }

            if (!!confirmMessage) {
                confirmMessage += '\n ' + this.translations['unsaved_changes_will_be_lost'] + ', ' + this.translations['are_you_sure_you_want_to_continue'].toLowerCase() + '?';

                shouldClose = await this.modal.openConfirmDialog({ type: 'warning', title: this.translations['you_have_unsaved_changes'], translateTitle: false, message: confirmMessage, translateMessage: false });
            }

            if (shouldClose === true) {
                this.dialog.close({ labelChanged: this.hasChangedLabel, sublabelChanged: this.hasChangedSublabel, helptextChanged: this.hasChangedHelptext });
            }
        }
    }

    public isLabelChangesSaved() {
        return JSON.stringify(this.labelEdits) === this.persistedLabelEdits;
    }

    public isSublabelChangesSaved() {
        return JSON.stringify(this.sublabelEdits) === this.persistedSublabelEdits;
    }

    public isHelpttextChangesSaved() {
        return JSON.stringify(this.helptextEdits) === this.persistedHelptextEdits;
    }

    public saveLabelTranslations = async (edits: any[]) => {
        this.persistedLabelEdits = JSON.stringify(await this.saveTranslations(edits));
        this.hasChangedLabel = true;
    };

    public saveSublabelTranslations = async (edits: any[]) => {
        this.persistedSublabelEdits = JSON.stringify(await this.saveTranslations(edits));
        this.hasChangedSublabel = true;
    };

    public saveHelptextTranslations = async (edits: any[]) => {
        this.persistedHelptextEdits = JSON.stringify(await this.saveTranslations(edits));
        this.hasChangedHelptext = true;
    };

    private async saveTranslations(edits: any[]) {
        this.dialog.disableClose = true;

        try {
            const response = await this.datatask.Post(62, { wordlang_names: edits });
            this.toast.addToast({ id: Date.now() + 'wordsave', title: response[0].ws_result_message, message: response[0].ws_result_message, type: 'success' });
        } finally {
            this.dialog.disableClose = false;
        }

        return edits;
    }

    private async loadLabelEdits() {
        this.labelEdits = await this.datatask.Post(61, { word_id: this.inputLabel });
        this.persistedLabelEdits = JSON.stringify(this.labelEdits);
    }

    private async loadSublabelEdits() {
        this.sublabelEdits = await this.datatask.Post(61, { word_id: this.inputSublabel });
        this.persistedSublabelEdits = JSON.stringify(this.sublabelEdits);
    }

    private async loadHelptextEdits() {
        this.helptextEdits = await this.datatask.Post(61, { word_id: this.inputHelptext });
        this.persistedHelptextEdits = JSON.stringify(this.helptextEdits);
    }

    private async translateWords() {
        const data = await this.translate.translateBatch(Object.keys(this.translations));

        Object.keys(data).forEach((key) => (this.translations[key] = data[key]));
    }
}

export interface LabelTranslationData {
    label: string;
    translateLabel: boolean;
    sublabel?: string;
    translateSublabel?: boolean;
    helptext?: string;
    translateHelptext?: boolean;
}
